import moment from 'moment';
import { CompositeModel } from './composite.model';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NotificationModel } from '../low-level/notification.model';
import { TimeModelOptions } from '../options/time-model-options';
import { TimeModel } from '../primitive/time.model';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigConstants } from '../../config/config-constants';
import { NotificationService } from '../../services/notification/notification.service';
import { ODataTimeZoneOutModel, TimeZoneModel } from './time-zone.model';

export class NotificationDurationModel extends CompositeModel {
    public startDateTime: TimeModel;
    public endDateTime: TimeModel;
    private timeZone: TimeZoneModel;

    /**
     * Duration model for changing duration.
     *
     * @param notification current notification data where duration needs to be updated
     */
    public constructor(private readonly notification: NotificationModel) {
        super('NotificationDurationModel');

        const injector = CustomInjector.getInjector();
        const notificationService = injector.get(NotificationService);

        const now = moment().startOf('minute');
        const startDate = notificationService.getNotificationStart(notification);
        const endDate = notificationService.getNotificationEnd(notification);
        this.timeZone = <TimeZoneModel>this.set(notification.timeZone);

        this.startDateTime = <TimeModel>(
            this.set(
                new TimeModel('StartTime', startDate, new TimeModelOptions({ maxValue: now.clone(), readonly: true })),
            )
        );

        this.endDateTime = <TimeModel>this.set(
            new TimeModel(
                'EndTime',
                endDate,
                new TimeModelOptions({
                    minValue: startDate.clone(),
                }),
            ),
        );
    }

    /**
     * Set the minimum value of the end date to the start date.
     */
    public setDateTimeOptions(): void {
        if (!this.startDateTime.value) {
            return;
        }
        this.endDateTime = assignModelOptions(
            this.endDateTime,
            {
                ...this.endDateTime.options,
                minValue: this.startDateTime.value.clone(),
            },
            this,
        );
    }

    public getOutModel(): ODataDurationOutModel {
        const notificationNo = this.notification.identifier.getQmnum();
        const taskNo = this.notification.identifier.getManum();
        const timeZoneOutModel: ODataTimeZoneOutModel = this.timeZone.getOutModel();

        const outModel: ODataDurationOutModel = {
            NotificationNo: notificationNo,
            TaskNumber: taskNo,
            FrontDate: this.startDateTime.value.format(ConfigConstants.formatDateNoTimezone),
            FrontTime: this.startDateTime.value.format(ConfigConstants.formatXSDTimeDuration),
            PlannedEndDate: this.endDateTime.value.format(ConfigConstants.formatDateNoTimezone),
            PlannedEndTime: this.endDateTime.value.format(ConfigConstants.formatXSDTimeDuration),
            ...timeZoneOutModel,
        };

        return outModel;
    }
}

export interface ODataDurationOutModel extends ODataObject, ODataTimeZoneOutModel {
    NotificationNo: string;
    TaskNumber: string;
    FrontDate: string;
    FrontTime: string;
    PlannedEndDate: string;
    PlannedEndTime: string;
}

export interface ODataDurationResponseModel extends ODataObject, ODataDurationOutModel {
    __metadata: {
        id: string;
        uri: string;
        type: string;
    };
}
